// ============================================================================
// IDEATION BUBBLE CHART
// Interactive Impact vs Complexity scatter chart using Recharts.
// Redesigned with quadrant labels, enhanced styling from Stitch design.
// Bubble size = viability score, color = priority label.
// ============================================================================

import React, { useCallback, useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceArea
} from 'recharts';
import type { Submission, PriorityLabel } from '../../services/ideationService';

interface IdeationBubbleChartProps {
  submissions: Submission[];
  selectedId?: string | null;
  onSelect?: (submissionId: string | null) => void;
}

const PRIORITY_COLORS: Record<PriorityLabel, string> = {
  'Quick Win': '#34d399',
  'Strategic Initiative': '#fbbf24',
  'Deprioritise': '#f87171',
  'Revisit Later': '#94a3b8',
};

const AXIS_LABELS = ['', 'Low', 'Med', 'High'];

interface ChartPoint {
  x: number;
  y: number;
  z: number;
  id: string;
  label: PriorityLabel;
  problem: string;
  submitter: string;
  complexity: number;
  viability: number;
  impact: number;
  composite: number;
}

const legendFormatter = (value: string) => <span className="text-xs font-medium text-slate-500">{value}</span>;

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload as ChartPoint;
  return (
    <div className="bg-slate-900 text-white rounded-xl px-4 py-3 shadow-xl max-w-xs text-sm border border-slate-700">
      <p className="font-semibold mb-1 line-clamp-2">{d.problem || 'No description'}</p>
      {d.submitter && <p className="text-slate-400 text-xs mb-2">by {d.submitter}</p>}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <span className="text-slate-400">Impact</span>
        <span className="font-medium">{AXIS_LABELS[d.impact] || d.impact}</span>
        <span className="text-slate-400">Complexity</span>
        <span className="font-medium">{AXIS_LABELS[d.complexity] || d.complexity}</span>
        <span className="text-slate-400">Viability</span>
        <span className="font-medium">{AXIS_LABELS[d.viability] || d.viability}</span>
        <span className="text-slate-400">Composite</span>
        <span className="font-medium">{d.composite}</span>
      </div>
      <div className="mt-2 pt-2 border-t border-slate-700">
        <span
          className="inline-block px-2 py-0.5 rounded-lg text-xs font-bold"
          style={{ backgroundColor: PRIORITY_COLORS[d.label] + '30', color: PRIORITY_COLORS[d.label] }}
        >
          {d.label}
        </span>
      </div>
    </div>
  );
};

export const IdeationBubbleChart: React.FC<IdeationBubbleChartProps> = ({
  submissions,
  selectedId,
  onSelect
}) => {
  const data: ChartPoint[] = useMemo(() => submissions.map(s => ({
    x: s.complexityScore + (Math.random() - 0.5) * 0.24,
    y: s.impactScore + (Math.random() - 0.5) * 0.24,
    z: s.viabilityScore,
    id: s.submissionId,
    label: s.priorityLabel,
    problem: (s.answers?.q1 || '').substring(0, 80),
    submitter: s.submitterName,
    complexity: s.complexityScore,
    viability: s.viabilityScore,
    impact: s.impactScore,
    composite: s.compositeScore
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [submissions]);

  const groups: Record<PriorityLabel, ChartPoint[]> = useMemo(() => {
    const g: Record<PriorityLabel, ChartPoint[]> = {
      'Quick Win': [],
      'Strategic Initiative': [],
      'Deprioritise': [],
      'Revisit Later': [],
    };
    data.forEach(d => { if (g[d.label]) g[d.label].push(d); });
    return g;
  }, [data]);

  const handleClick = useCallback((point: any) => {
    if (point && point.id && onSelect) {
      onSelect(selectedId === point.id ? null : point.id);
    }
  }, [selectedId, onSelect]);

  if (submissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 text-sm">
        No submissions to chart yet
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={420}>
      <ScatterChart margin={{ top: 30, right: 30, bottom: 30, left: 30 }}>
        {/* Quadrant background shading */}
        <ReferenceArea x1={0.5} x2={2} y1={2} y2={3.5} fill="#34d399" fillOpacity={0.04} />
        <ReferenceArea x1={2} x2={3.5} y1={2} y2={3.5} fill="#fbbf24" fillOpacity={0.04} />
        <ReferenceArea x1={0.5} x2={2} y1={0.5} y2={2} fill="#94a3b8" fillOpacity={0.04} />
        <ReferenceArea x1={2} x2={3.5} y1={0.5} y2={2} fill="#f87171" fillOpacity={0.04} />

        <XAxis
          dataKey="x"
          type="number"
          name="Complexity"
          domain={[0.5, 3.5]}
          ticks={[1, 2, 3]}
          tickFormatter={(v: number) => AXIS_LABELS[Math.round(v)] || ''}
          label={{ value: 'Complexity', position: 'insideBottom', offset: -15, style: { fill: '#052831', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' } }}
          stroke="#e2e8f0"
          tick={{ fill: '#94a3b8', fontSize: 11 }}
        />
        <YAxis
          dataKey="y"
          type="number"
          name="Impact"
          domain={[0.5, 3.5]}
          ticks={[1, 2, 3]}
          tickFormatter={(v: number) => AXIS_LABELS[Math.round(v)] || ''}
          label={{ value: 'Impact', angle: -90, position: 'insideLeft', offset: 0, style: { fill: '#052831', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' } }}
          stroke="#e2e8f0"
          tick={{ fill: '#94a3b8', fontSize: 11 }}
        />
        <ZAxis dataKey="z" type="number" range={[150, 600]} name="Viability" />
        <Tooltip content={<CustomTooltip />} cursor={false} />
        <Legend
          wrapperStyle={{ paddingTop: 16 }}
          formatter={legendFormatter}
        />

        {(Object.entries(groups) as [PriorityLabel, ChartPoint[]][]).map(([label, points]) =>
          points.length > 0 ? (
            <Scatter
              key={label}
              name={label}
              data={points}
              fill={PRIORITY_COLORS[label]}
              onClick={handleClick}
              cursor="pointer"
            >
              {points.map((point) => (
                <Cell
                  key={point.id}
                  fill={PRIORITY_COLORS[label]}
                  stroke={selectedId === point.id ? '#052831' : 'white'}
                  strokeWidth={selectedId === point.id ? 3 : 2}
                  fillOpacity={selectedId && selectedId !== point.id ? 0.25 : 0.8}
                />
              ))}
            </Scatter>
          ) : null
        )}
      </ScatterChart>
    </ResponsiveContainer>
  );
};
